import {
  DEVELOPER_MODE_CHANGE_EVENT,
  DEVELOPER_MODE_STORAGE_KEY,
  getDeveloperMode,
  storeDeveloperMode,
} from "modules/shared/lib/developerMode"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { createContext, useEffect, useSyncExternalStore } from "react"
import { BackofficeType } from "schema"

type DeveloperModeContextType = {
  developerMode: boolean
  setDeveloperMode: (value: boolean) => void
}

const developerModeContext = createContext<DeveloperModeContextType | undefined>(undefined)

function subscribeToDeveloperMode(onStoreChange: () => void) {
  function onStorage(event: StorageEvent) {
    if (event.key === DEVELOPER_MODE_STORAGE_KEY) {
      onStoreChange()
    }
  }

  window.addEventListener(DEVELOPER_MODE_CHANGE_EVENT, onStoreChange)
  window.addEventListener("storage", onStorage)

  return () => {
    window.removeEventListener(DEVELOPER_MODE_CHANGE_EVENT, onStoreChange)
    window.removeEventListener("storage", onStorage)
  }
}

export const DeveloperModeProvider = ({ children }: React.PropsWithChildren) => {
  const developerMode = useSyncExternalStore(subscribeToDeveloperMode, getDeveloperMode)
  const { data: employee } = useAuthEmployeeQuery()

  function setDeveloperMode(value: boolean) {
    storeDeveloperMode(value)
  }

  useEffect(() => {
    const isSuperadmin = employee?.backofficeType === BackofficeType.Superadmin

    if (!isSuperadmin) return

    function onKeyDown(event: KeyboardEvent) {
      if (
        event.repeat ||
        event.key?.toLowerCase() !== "d" ||
        !event.shiftKey ||
        (!event.metaKey && !event.ctrlKey)
      ) {
        return
      }

      event.preventDefault()
      storeDeveloperMode(!developerMode)
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [developerMode, employee?.backofficeType])

  return (
    <developerModeContext.Provider value={{ developerMode, setDeveloperMode }}>
      {children}
    </developerModeContext.Provider>
  )
}

DeveloperModeProvider.context = developerModeContext
